import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './FeaturedCards3D.css';

const FeaturedCards3D = ({ sliders, onCardClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-scroll functionality
  useEffect(() => {
    if (!sliders.length || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % sliders.length);
    }, 1500);
    
    return () => clearInterval(interval);
  }, [sliders.length, isPaused]);

  const handleDragEnd = (event, info) => {
    const threshold = 100;
    if (info.offset.x > threshold && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (info.offset.x < -threshold && currentIndex < sliders.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    setDragOffset(0);
  };

  const goToPrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : sliders.length - 1);
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev + 1) % sliders.length);
  };

  const getCardStyle = (index) => {
    const offset = index - currentIndex;
    const isActive = offset === 0;
    
    return {
      x: offset * 120 + dragOffset * 0.1,
      z: isActive ? 100 : -Math.abs(offset) * 50,
      rotateY: offset * -15,
      rotateX: Math.abs(offset) * 5,
      scale: isActive ? 1.1 : 1 - Math.abs(offset) * 0.1,
      opacity: Math.abs(offset) > 2 ? 0 : 1 - Math.abs(offset) * 0.2,
      filter: Math.abs(offset) > 0 ? `brightness(${0.7 - Math.abs(offset) * 0.1}) blur(${Math.abs(offset) * 0.5}px)` : 'brightness(1)'
    };
  };

  return (
    <div 
      className="featured-cards-3d"
      style={{ 
        width: '100%', 
        maxWidth: '1200px', 
        margin: '0 auto',
        overflow: 'hidden'
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="cards-container">
        <button className="nav-btn nav-btn-left" onClick={goToPrevious}>
          ‹
        </button>
        
        <div className="cards-perspective">
          {sliders.map((slider, index) => {
            const isActive = index === currentIndex;
            const cardStyle = getCardStyle(index);
            
            return (
              <motion.div
                key={slider.id}
                className={`card-3d ${isActive ? 'active' : ''}`}
                animate={cardStyle}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDrag={(event, info) => setDragOffset(info.offset.x)}
                onDragEnd={handleDragEnd}
                onClick={() => onCardClick && onCardClick(slider)}
                whileHover={{ scale: isActive ? 1.15 : 1.05 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 200, 
                  damping: 25,
                  mass: 0.8,
                  restDelta: 0.001
                }}
              >
                <div className="card-background">
                  <img src={slider.background_image} alt={slider.name} />
                  <div className="card-gradient"></div>
                </div>
                
                <AnimatePresence>
                  {isActive && slider.overlay_image && (
                    <motion.div
                      className="character-popup"
                      initial={{ z: 0, scale: 1, y: 0 }}
                      animate={{ z: 150, scale: 1.2, y: -20 }}
                      exit={{ z: 0, scale: 1, y: 0 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 300, 
                        damping: 20,
                        mass: 0.6
                      }}
                    >
                      <img src={slider.overlay_image} alt={slider.name} />
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="card-content">
                  <h3 className={isActive ? 'active-title' : ''}>{slider.name}</h3>
                  {slider.description && <p className="description">{slider.description}</p>}
                  {slider.button_text && (
                    <div className="card-button">
                      <span>{slider.button_text}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
        
        <button className="nav-btn nav-btn-right" onClick={goToNext}>
          ›
        </button>
      </div>
      
      <div className="navigation-dots">
        {sliders.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default FeaturedCards3D;